import { useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { Outlet, useNavigate } from 'react-router-dom';
import { GoPlus } from 'react-icons/go';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { MdContentCopy, MdOutlineRemoveRedEye, MdOutlineVisibilityOff } from 'react-icons/md';
import copy from 'copy-to-clipboard';

import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import { formatDate } from 'utils/helperFunctions';
import { BusinessApiKey, useDeleteBusinessApiKey, useGetBusinessApiKeys } from '@/apis/business/setting/apiKeys/hook';

const MASKED_KEY = '*************************************************';

function StatusBadge({ status }: { status: string | boolean }) {
  const theme = useTheme();
  const isActive =
    status === true || String(status).toLowerCase() === 'true' || String(status).toLowerCase() === 'active';
  return (
    <Chip
      label={isActive ? 'Active' : 'Inactive'}
      size='small'
      sx={{
        fontWeight: 450,
        fontSize: 12,
        borderRadius: '14px',
        color: isActive ? theme.palette.primary.main : theme.palette.error.main,
        bgcolor: isActive ? theme.palette.primary.light : theme.palette.error.light
      }}
    />
  );
}

function KeyValueCell({ apiKey }: { apiKey: string | undefined }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const value = apiKey ?? '';

  return (
    <Box display='flex' alignItems='center' gap={0.5}>
      <Typography color='#000' letterSpacing={visible ? 0.5 : 1.5}>
        {visible && value ? value : MASKED_KEY}
      </Typography>
      <IconButton
        size='small'
        aria-label={visible ? 'Hide API key' : 'Show API key'}
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? <MdOutlineVisibilityOff size={20} color='#000' /> : <MdOutlineRemoveRedEye size={20} color='#000' />}
      </IconButton>
      <Tooltip title={copied ? 'Copied' : 'Copy'} open={copied || undefined} onClose={() => setCopied(false)}>
        <span>
          <IconButton
            size='small'
            aria-label='Copy API key'
            disabled={!value}
            onClick={() => {
              copy(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            <MdContentCopy size={18} color='#000' />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

export default function ApiKeys() {
  const navigate = useNavigate();
  const { currentPage, perPage, setPage, setPerPage } = usePagination();
  const { data: apiKeys, isLoading } = useGetBusinessApiKeys({
    page: currentPage,
    per_page: perPage
  });
  const { mutate: deleteApiKey } = useDeleteBusinessApiKey();

  const totalPages = apiKeys?.meta?.pagination?.total_pages ?? apiKeys?.meta?.total_pages ?? 1;
  const totalCount = apiKeys?.meta?.pagination?.total_count ?? apiKeys?.meta?.total_count ?? 0;

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography variant='h6' fontWeight={500}>
          API Keys
        </Typography>
        <Button onClick={() => navigate('add')} variant='contained' color='primary' startIcon={<GoPlus />}>
          Add Key
        </Button>
      </Box>

      <DataTable data={apiKeys?.data ?? []} columns={columns(deleteApiKey)} isHeaderSticky isLoading={isLoading} />

      <Pagination
        totalPages={totalPages}
        totalCount={totalCount}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />

      <Outlet />
    </>
  );
}

type DeleteFn = (id: number | string) => void;

const columns = (deleteApiKey: DeleteFn): ColumnDef<BusinessApiKey>[] => [
  {
    header: 'NAME',
    size: 240,
    cell: ({ row }) => (
      <Box display='flex' alignItems='center' gap={1} flexWrap='wrap'>
        <Typography>{row.original.name}</Typography>
        {row.original.status != null && row.original.status !== '' && <StatusBadge status={row.original.status} />}
      </Box>
    )
  },
  {
    header: 'KEY VALUE',
    size: 360,
    cell: ({ row }) => <KeyValueCell apiKey={row.original.key} />
  },
  {
    header: 'CREATED AT',
    size: 180,
    cell: ({ row }) => <Typography>{formatDate(row.original.created_at, 'MM/DD/YYYY, HH:mm')}</Typography>
  },
  {
    header: 'ACTION',
    size: 80,
    cell: ({ row }) => (
      <IconButton onClick={() => deleteApiKey(row.original.id)} color='error' aria-label='Delete API key'>
        <RiDeleteBin6Line style={{ color: 'red' }} />
      </IconButton>
    )
  }
];
